var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from "react";
import { throttle, trimSlash, trimLeadingSlash, hasValue, SpacingMode, Environment, toNum, toJSON, toBool } from '@utils';
import { eventDispatcher } from '@realtime/eventbus/useEventBus';
import { getEnabledLanguages, localize, updateDirectionBasedOnLanguage } from '@localisation/localizationUtils';
import { defaultLightTheme, getTheme, onChangeTheme } from '@theme/themeUtils';
import { CacheManager, shouldUseCache, getCacheTime, resolveCacheKey } from './cacheHelper';
import { executeBatchQueue } from './batchExecutor';
import { executeBatchWithCachingForResult, executeAPICall, checkCacheBeforeExecutionForResult, cacheData, trackPendingRequest, normalizeArgs } from './executionHelpers';
const BATCH_TIME = 100;
// The public user of the public page or dashboard on screen. See the apiKey / userKey accessors below.
// accountApiKey / accountUserKey: the account public user, used when the page or dashboard has none
// of its own (see setPublicCredentials). Set once, at init, for a signed-out session only.
const publicCredentials = { apiKey: '', userKey: '', accountApiKey: '', accountUserKey: '' };
class DefaultDashboardContext {
    /*
     * apiKey and userKey are accessors, not plain fields, for one reason: public dashboards.
     *
     * A signed-out visitor has no key. A public dashboard runs as a chosen "public user", and
     * that user's key must be used for every call made while the dashboard is on screen.
     *
     * The provider (UXPContextWrapper) builds a new context instance on every render. A key
     * stored as a plain field lives on one instance only: the next instance does not have it,
     * and a component still holding an older instance does not have it either. Calls then go
     * out with no user and are refused.
     *
     * So the public user's key is kept in `publicCredentials`, outside the instances, and the
     * getters read it at the moment of each call. Every instance, old or new, gives the same answer.
     *
     *  - Signed-in session: returns the session's own key (_apiKey / _userKey). Unchanged.
     *  - Signed-out session on a public dashboard: returns the public user's key.
     *  - Signed-out session anywhere else: returns the empty own key.
     *
     * The setters keep `context.apiKey = token` working (the sign-in code sets it).
     * Do not copy a context with spread or Object.assign: accessors are not copied.
     */
    get apiKey() { return this.isPublicSession && publicCredentials.apiKey ? publicCredentials.apiKey : this._apiKey; }
    set apiKey(value) { this._apiKey = value; }
    get userKey() { return this.isPublicSession && publicCredentials.apiKey ? publicCredentials.userKey : this._userKey; }
    set userKey(value) { this._userKey = value; }
    constructor(partialContext) {
        var _a, _b;
        this.actionQueue = [];
        this.watchList = {};
        this.pendingRequests = new Map();
        this.cacheManager = CacheManager.getInstance();
        this.pendingRequests = new Map();
        // Cleanup expired cache on initialization
        this.cacheManager.cleanupExpiredCache();
        const tn = hasValue(partialContext === null || partialContext === void 0 ? void 0 : partialContext.themeName) ? partialContext === null || partialContext === void 0 ? void 0 : partialContext.themeName : defaultLightTheme;
        const { name: activeThemeName, config: activeTheme, type: activeThemeType } = getTheme(tn, (partialContext === null || partialContext === void 0 ? void 0 : partialContext.customThemes) || {});
        const _languages = getEnabledLanguages((partialContext === null || partialContext === void 0 ? void 0 : partialContext.enabledLanguages) || []);
        const _language = hasValue(partialContext === null || partialContext === void 0 ? void 0 : partialContext.language) ? partialContext === null || partialContext === void 0 ? void 0 : partialContext.language : 'en';
        this.environment = ((partialContext === null || partialContext === void 0 ? void 0 : partialContext.environment) == Environment.Dev) ? Environment.Dev : Environment.Prod;
        this.root = partialContext === null || partialContext === void 0 ? void 0 : partialContext.root;
        this.lucyUrl = partialContext === null || partialContext === void 0 ? void 0 : partialContext.lucyUrl;
        this.fullAccountUrl = partialContext === null || partialContext === void 0 ? void 0 : partialContext.fullAccountUrl;
        this.apiKey = partialContext === null || partialContext === void 0 ? void 0 : partialContext.apiKey;
        this.onAPIKeyChange = partialContext === null || partialContext === void 0 ? void 0 : partialContext.onAPIKeyChange;
        this.language = _language;
        this.enabledLanguages = _languages;
        this.$L = this._localize.bind(this);
        this.direction = updateDirectionBasedOnLanguage(_language, _languages);
        this.themeName = activeThemeName;
        this.themeType = activeThemeType;
        this.theme = activeTheme;
        ;
        this.customThemes = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.customThemes) || {};
        this.setTheme = partialContext === null || partialContext === void 0 ? void 0 : partialContext.setTheme;
        this.onChangeTheme = this._changeTheme.bind(this);
        this.authConfig = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.authConfig) || null;
        this.userKey = partialContext === null || partialContext === void 0 ? void 0 : partialContext.userKey;
        this.isPublicSession = (_a = partialContext === null || partialContext === void 0 ? void 0 : partialContext.isPublicSession) !== null && _a !== void 0 ? _a : false;
        if (this.isPublicSession && ((_b = partialContext === null || partialContext === void 0 ? void 0 : partialContext.publicUser) === null || _b === void 0 ? void 0 : _b.apiKey)) {
            publicCredentials.accountApiKey = partialContext.publicUser.apiKey;
            publicCredentials.accountUserKey = partialContext.publicUser.userKey || '';
        }
        this.loginType = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.loginType) || '';
        this.loginUrl = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.loginUrl) || null;
        this.logoutUrl = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.logoutUrl) || null;
        this.appRoles = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.appRoles) || {};
        this.enabledApps = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.enabledApps) || [];
        this.userDetails = partialContext === null || partialContext === void 0 ? void 0 : partialContext.userDetails;
        this.getUserDetails = this._getUserDetails;
        this.userDataFormat = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.userDataFormat) || null;
        this.userTimeFormat = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.userTimeFormat) || null;
        this.userCurrencyFormat = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.userCurrencyFormat) || null;
        this.userSiteTimeZoneName = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.userSiteTimeZoneName) || null;
        this.userTimezoneCode = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.userTimezoneCode) || null;
        this.userTimezoneAbbreviation = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.userTimezoneAbbreviation) || '';
        // keep 0 (UTC sites); a missing/invalid offset stays undefined so formatters fall back to browser time
        const rawOffset = partialContext === null || partialContext === void 0 ? void 0 : partialContext.userSiteTimeZoneOffsetInSeconds;
        const siteOffset = rawOffset === null || rawOffset === undefined || rawOffset === '' ? NaN : Number(rawOffset);
        this.userSiteTimeZoneOffsetInSeconds = isFinite(siteOffset) ? siteOffset : undefined;
        this.userSiteKey = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.userSiteKey) || '';
        this.userSiteName = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.userSiteName) || '';
        this.userSiteImageKey = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.userSiteImageKey) || '';
        this.userSiteImageUrl = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.userSiteImageUrl) || '';
        this.accountDisplayName = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.accountDisplayName) || '';
        this.useGoogleTranslate = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.useGoogleTranslate) || false;
        this.hasTOS = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.hasTOS) || false;
        this.tosAgreed = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.tosAgreed) || false;
        this.agreementAgreed = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.agreementAgreed) || false;
        this.hasAgreement = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.hasAgreement) || false;
        this.scriptFiles = partialContext === null || partialContext === void 0 ? void 0 : partialContext.scriptFiles;
        this.navigationLinks = partialContext === null || partialContext === void 0 ? void 0 : partialContext.navigationLinks;
        this.routes = partialContext === null || partialContext === void 0 ? void 0 : partialContext.routes;
        this.restrictedRoutes = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.restrictedRoutes) || [];
        this.unavailableRoutes = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.unavailableRoutes) || {};
        this.configuredPages = partialContext === null || partialContext === void 0 ? void 0 : partialContext.configuredPages;
        this.isLoading = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.isLoading) || false;
        this.loadingMessage = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.loadingMessage) || 'Loading';
        this.setLoading = partialContext === null || partialContext === void 0 ? void 0 : partialContext.setLoading;
        this.marketplaceUrl = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.marketplaceUrl) || null;
        this.loadWidgetsFromIviva = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.loadWidgetsFromIviva) || false;
        this.widgetSearchAPI = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.widgetSearchAPI) || null;
        this.maxFileUploadSize = toNum(partialContext === null || partialContext === void 0 ? void 0 : partialContext.maxFileUploadSize, 0);
        // Left undefined when the host never sent it. uxp ships from a CDN and
        // reaches frameworks older than these settings, and `undefined` is how
        // the upload hook tells that apart from a framework that sent 0.
        this.largeUploadFileSizeMB = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.largeUploadFileSizeMB) === undefined
            ? undefined
            : toNum(partialContext.largeUploadFileSizeMB, 0);
        this.uploadChunkSizeMB = toNum(partialContext === null || partialContext === void 0 ? void 0 : partialContext.uploadChunkSizeMB, 1);
        // Left undefined when the host never sent it. Then no page view is sent.
        this.pageViewTracking = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.pageViewTracking) === undefined
            ? undefined
            : toBool(partialContext.pageViewTracking);
        this.trackAnalytics = partialContext === null || partialContext === void 0 ? void 0 : partialContext.trackAnalytics;
        this.loaded = partialContext === null || partialContext === void 0 ? void 0 : partialContext.loaded;
        this.executeQueuedItems = throttle(this._executeQueuedItems.bind(this), BATCH_TIME, { leading: false });
        this.updateAPIKey = this._updateAPIKey.bind(this);
        this.setLoginUIRef = this._setLoginUIRef.bind(this);
        this.spacingMode = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.spacingMode) || SpacingMode.Compact;
        this.title = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.title) || null;
        this.viewOverrides = (partialContext === null || partialContext === void 0 ? void 0 : partialContext.viewOverrides) || {};
        this.setViewOverrides = partialContext === null || partialContext === void 0 ? void 0 : partialContext.setViewOverrides;
        this.refreshViewOverrides = this._refreshViewOverrides.bind(this);
        const normalizeObjectTabs = (raw) => Object.fromEntries(Object.entries(raw || {}).map(([key, tabs]) => [
            key,
            (tabs || []).map((tab) => (Object.assign(Object.assign({}, tab), { components: Array.isArray(tab.components)
                    ? tab.components
                    : (toJSON(tab.components, []) || []) })))
        ]));
        this.objectTabs = normalizeObjectTabs((partialContext === null || partialContext === void 0 ? void 0 : partialContext.objectTabs) || {});
        // App-declared tabs (bundle.json) — the deterministic default set delivered at bootstrap.
        this.declaredObjectTabs = normalizeObjectTabs((partialContext === null || partialContext === void 0 ? void 0 : partialContext.declaredObjectTabs) || {});
        this.setObjectTabs = partialContext === null || partialContext === void 0 ? void 0 : partialContext.setObjectTabs;
        this.refreshObjectTabs = this._refreshObjectTabs.bind(this);
    }
    _onConfigChanges(changes) {
        return __awaiter(this, void 0, void 0, function* () {
            // Persist theme change to backend (fire-and-forget)
            this.executeServiceSafe('User', 'User:UpdateTheme', { UserKey: this.userKey, Theme: changes.themeName });
        });
    }
    _changeTheme(theme, rootElementId) {
        const { name, config, type } = onChangeTheme(theme, this.customThemes, rootElementId);
        if (this.setTheme)
            this.setTheme(name, config, type);
        // save if not public session
        if (!this.isPublicSession)
            this._onConfigChanges({ themeName: name });
        eventDispatcher('uxp-core-dashboard-context', 'uxp-theme-changed', { themeName: name });
    }
    _localize(code, params) {
        return localize(this.language, code, params);
    }
    _updateAPIKey(token) {
        this.apiKey = token;
        if (this.onAPIKeyChange) {
            this.onAPIKeyChange(token);
        }
    }
    // The page's or dashboard's own public user; with none (empty apiKey), the account public user.
    setPublicCredentials(apiKey, userKey) {
        const own = !!apiKey;
        publicCredentials.apiKey = own ? apiKey : publicCredentials.accountApiKey;
        publicCredentials.userKey = own ? userKey : publicCredentials.accountUserKey;
    }
    _setLoginUIRef(ref) {
        this.loginUIRef = ref;
    }
    _getUserDetails(updateContext) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const res = yield this.executeService('User', 'User:Details', { UserKey: this.userKey }, { json: true });
                if (!!(res === null || res === void 0 ? void 0 : res[0]) && updateContext)
                    this.userDetails = res;
                return (res === null || res === void 0 ? void 0 : res[0]) || null;
            }
            catch (e) {
                console.log('Unable to get user details, error', e);
                return null;
            }
        });
    }
    _refreshViewOverrides() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.executeService('System', 'GetViewOverrides', {}, { json: true })
                .then(res => {
                var _a;
                const newOverrides = toJSON((_a = res === null || res === void 0 ? void 0 : res[0]) === null || _a === void 0 ? void 0 : _a.viewoverrides, {});
                this.viewOverrides = newOverrides;
                if (this.setViewOverrides) {
                    this.setViewOverrides(newOverrides);
                }
            });
        });
    }
    _refreshObjectTabs() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.executeService('System', 'GetObjectTabs', {}, { json: true })
                .then(res => {
                var _a;
                const raw = toJSON((_a = res === null || res === void 0 ? void 0 : res[0]) === null || _a === void 0 ? void 0 : _a.objecttabs, {});
                const newTabs = Object.fromEntries(Object.entries(raw).map(([key, tabs]) => [
                    key,
                    (tabs || []).map((tab) => (Object.assign(Object.assign({}, tab), { components: Array.isArray(tab.components)
                            ? tab.components
                            : (toJSON(tab.components, []) || []) })))
                ]));
                this.objectTabs = newTabs;
                if (this.setObjectTabs) {
                    this.setObjectTabs(newTabs);
                }
            });
        });
    }
    _executeQueuedItems(item) {
        // if a item is passed, use it as a queue item (to execute it immediately without batching)
        // else take from queue
        let queue = [];
        if (item) {
            queue = [item];
        }
        else {
            queue = this.actionQueue;
            this.actionQueue = [];
        }
        executeBatchQueue(queue, this.lucyUrl, this.apiKey, this.watchList, this.isPublicSession ? null : this.loginUIRef, this.isPublicSession, this.language);
    }
    /**
     * legacy execute functions
     * These will return a Promise<any> and throw on error
     * Wraps safe methods for backward compatibility
     */
    executeAction(model, action, parameters, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.executeActionSafe(model, action, parameters, options);
            if (result.error)
                throw result.error;
            return result.data;
        });
    }
    executeService(app, service, parameters, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.executeServiceSafe(app, service, parameters, options);
            if (result.error)
                throw result.error;
            return result.data;
        });
    }
    executeModelCollectionService(model, collection, parameters, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.executeModelCollectionServiceSafe(model, collection, parameters, options);
            if (result.error)
                throw result.error;
            return result.data;
        });
    }
    fromLucyDataCollection(model, collection) {
        return (max, last, args) => __awaiter(this, void 0, void 0, function* () {
            const parsedLast = hasValue(last, true) ? toNum(last) : 0;
            const filters = JSON.stringify(normalizeArgs(args));
            const params = {
                modelName: model,
                collectionName: collection,
                max: max,
                last: parsedLast,
                filter: filters
            };
            try {
                const res = yield this.executeModelCollectionService(model, collection, params);
                let _res = JSON.parse(res);
                let _data = JSON.parse(_res[0].data);
                let _pageToken = (parseInt(params.last) + _data.length).toString();
                return ({ items: _data, pageToken: _pageToken });
            }
            catch (e) {
                console.log("Exception : ", e);
                throw e;
            }
        });
    }
    // Backward compatibility wrapper
    executeComponent(component, route, method, params, body, additionalHeaders, additionalConfigurations) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.executeMicroServiceSafe({
                serviceName: component,
                route: route,
                method: method,
                params: params,
                data: body,
                configurations: Object.assign({ headers: additionalHeaders }, additionalConfigurations)
            });
            if (result.errorMessage || result.data === null) {
                throw result.error || new Error(result.errorMessage);
            }
            return result.data;
        });
    }
    executeQuery(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { data, error } = yield this.executeQuerySafe(params);
            if (error) {
                throw error;
            }
            return data;
        });
    }
    /**
     * new execution functions that return Promise<ExecutionResult<T>>
     * no need to try/catch
     */
    executeAPISafe(config, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const url = ((_a = config.url) === null || _a === void 0 ? void 0 : _a.startsWith('http'))
                ? config.url
                : `${trimSlash(this.lucyUrl)}/${trimLeadingSlash(config.url || '')}`;
            const useCache = shouldUseCache(options);
            const cacheKey = useCache ? resolveCacheKey(options, 'api', `${config.method || 'get'}_${url}`, { params: config.params || null, data: config.data || null }) : null;
            // Check cache and pending requests
            const cachedOrPending = checkCacheBeforeExecutionForResult(cacheKey, this.cacheManager, this.pendingRequests, 'api', url);
            if (cachedOrPending)
                return cachedOrPending;
            const executionPromise = (() => __awaiter(this, void 0, void 0, function* () {
                const result = yield executeAPICall(Object.assign(Object.assign({}, config), { url, headers: Object.assign({ 'Authorization': this.apiKey ? `APIKEY ${this.apiKey}` : '' }, config.headers || {}) }));
                if (!(result === null || result === void 0 ? void 0 : result.error)) {
                    const responseData = result.data;
                    // Cache the response if caching is enabled
                    cacheData(cacheKey, responseData, this.cacheManager, getCacheTime(options), 'api', url);
                }
                return result;
            }))();
            // Track pending requests
            return trackPendingRequest(cacheKey, executionPromise, this.pendingRequests);
        });
    }
    executeActionSafe(model, action, parameters, options) {
        return executeBatchWithCachingForResult('action', `${model}_${action}`, (requestId, wrappedResolve, wrappedReject) => ({
            model,
            action,
            parameters,
            id: requestId,
            callback: wrappedResolve,
            error: wrappedReject,
            options
        }), parameters, options, {
            cacheManager: this.cacheManager,
            pendingRequests: this.pendingRequests,
            watchList: this.watchList,
            actionQueue: this.actionQueue,
            executeQueuedItems: this.executeQueuedItems,
            _executeQueuedItems: this._executeQueuedItems.bind(this)
        });
    }
    executeServiceSafe(app, service, parameters, options) {
        return executeBatchWithCachingForResult('service', `${app}_${service}`, (requestId, wrappedResolve, wrappedReject) => ({
            app,
            service,
            parameters,
            id: requestId,
            callback: wrappedResolve,
            error: wrappedReject,
            options
        }), parameters, options, {
            cacheManager: this.cacheManager,
            pendingRequests: this.pendingRequests,
            watchList: this.watchList,
            actionQueue: this.actionQueue,
            executeQueuedItems: this.executeQueuedItems,
            _executeQueuedItems: this._executeQueuedItems.bind(this)
        });
    }
    publishNotification(params) {
        return __awaiter(this, void 0, void 0, function* () {
            const { notificationId, objectType, severity, message, objectKey, internalState, additionalParams, allowedUserKeys } = params;
            const serviceParams = { notificationId, objectType, status: 'NEW' };
            if (severity != null)
                serviceParams.severity = severity;
            if (message != null)
                serviceParams.message = message;
            if (objectKey != null)
                serviceParams.objectKey = objectKey;
            if (internalState != null)
                serviceParams.internalState = internalState;
            if (additionalParams != null) {
                serviceParams.additionalParams = typeof additionalParams === 'string'
                    ? additionalParams
                    : JSON.stringify(additionalParams);
            }
            if (allowedUserKeys != null)
                serviceParams.allowedUserKeys = allowedUserKeys.join(',');
            const result = yield this.executeServiceSafe('System', 'UpsertNotification', serviceParams, { json: true });
            if (result.error || !result.data)
                return { data: null, error: result.error, errorMessage: result.errorMessage };
            const row = Array.isArray(result.data) ? result.data[0] : result.data;
            return { data: { notificationID: row.notificationID, created: String(row.created).toLowerCase() !== 'false' } };
        });
    }
    resolveNotifications(filters) {
        return __awaiter(this, void 0, void 0, function* () {
            const { notificationIds, objectType, objectKey } = filters;
            const serviceParams = {};
            if (notificationIds && notificationIds.length)
                serviceParams.notificationIds = notificationIds.join(',');
            if (objectType != null)
                serviceParams.objectType = objectType;
            if (objectKey != null)
                serviceParams.objectKey = objectKey;
            const result = yield this.executeServiceSafe('System', 'ResolveNotifications', serviceParams, { json: true });
            if (result.error || !result.data)
                return { data: null, error: result.error, errorMessage: result.errorMessage };
            const row = Array.isArray(result.data) ? result.data[0] : result.data;
            return { data: { resolvedCount: Number(row.resolvedCount) || 0 } };
        });
    }
    // ponytail: objectType kept for signature compat; resolve-by-id is exact, no need to AND a type filter
    resolveNotification(notificationId, _objectType) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.resolveNotifications({ notificationIds: [notificationId] });
            if (result.error || !result.data)
                return { data: null, error: result.error, errorMessage: result.errorMessage };
            return { data: { notificationID: notificationId } };
        });
    }
    executeModelCollectionServiceSafe(model, collection, parameters, options) {
        return executeBatchWithCachingForResult('collection', `${model}_${collection}`, (requestId, wrappedResolve, wrappedReject) => ({
            modelName: model,
            collectionName: collection,
            parameters,
            id: requestId,
            callback: wrappedResolve,
            error: wrappedReject,
            options
        }), parameters, options, {
            cacheManager: this.cacheManager,
            pendingRequests: this.pendingRequests,
            watchList: this.watchList,
            actionQueue: this.actionQueue,
            executeQueuedItems: this.executeQueuedItems,
            _executeQueuedItems: this._executeQueuedItems.bind(this)
        });
    }
    executeMicroServiceSafe(config, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const { serviceName, method, params, data, configurations } = config;
            const _b = configurations || {}, { headers = {} } = _b, otherConfig = __rest(_b, ["headers"]);
            const route = ((_a = config === null || config === void 0 ? void 0 : config.route) === null || _a === void 0 ? void 0 : _a.startsWith('/')) ? config.route : '/' + config.route;
            const useCache = shouldUseCache(options);
            const cacheKey = useCache ? resolveCacheKey(options, 'microservice', `${serviceName}_${route}_${method}`, { params: params || null, body: data || null }) : null;
            // Check cache and pending requests
            const cachedOrPending = checkCacheBeforeExecutionForResult(cacheKey, this.cacheManager, this.pendingRequests, 'microservice', `${serviceName}${route}`);
            if (cachedOrPending)
                return cachedOrPending;
            const executionPromise = (() => __awaiter(this, void 0, void 0, function* () {
                const config = Object.assign({ url: `${trimSlash(this.lucyUrl)}/components/${serviceName}${route}`, method: method, headers: Object.assign({ 'Authorization': this.apiKey ? `APIKEY ${this.apiKey}` : '', 'Content-Type': 'application/json' }, headers || {}), params: params || {} }, otherConfig);
                if (method !== 'get') {
                    config.data = data || {};
                }
                const result = yield executeAPICall(config);
                if (!(result === null || result === void 0 ? void 0 : result.error)) {
                    const responseData = result.data;
                    // Cache the response if caching is enabled
                    cacheData(cacheKey, responseData, this.cacheManager, getCacheTime(options), 'microservice', `${serviceName}${route}`);
                }
                return result;
            }))();
            // Track pending requests
            return trackPendingRequest(cacheKey, executionPromise, this.pendingRequests);
        });
    }
    executeQuerySafe(params) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c;
            const { data, error, errorMessage } = yield this.executeServiceSafe('Lucy', 'Query:FindQuery', params, { json: true, executeImmidiately: true });
            if (!!error) {
                return {
                    data: null,
                    error: error,
                    errorMessage: errorMessage
                };
            }
            if (!data || !data[0]) {
                return {
                    data: null,
                    error: new Error('Query not found'),
                    errorMessage: 'Query not found'
                };
            }
            const query = toJSON((_a = data[0]) === null || _a === void 0 ? void 0 : _a.Configuration, {});
            if (!(query === null || query === void 0 ? void 0 : query.query) || ((_c = (_b = query === null || query === void 0 ? void 0 : query.query) === null || _b === void 0 ? void 0 : _b.sources) === null || _c === void 0 ? void 0 : _c.length) == 0) {
                return {
                    data: null,
                    error: new Error('Invalid query'),
                    errorMessage: 'Invalid query'
                };
            }
            // execute query
            return this.executeMicroServiceSafe({
                serviceName: 'QueryEngine',
                route: '/v1/execution',
                method: 'post',
                data: (query === null || query === void 0 ? void 0 : query.query) || {}
            });
        });
    }
    /**
     * handle permissions
     */
    hasApp(appName) {
        var _a;
        return ((_a = this.enabledApps) === null || _a === void 0 ? void 0 : _a.includes(appName)) || false;
    }
    hasAppRole(app, role) {
        var _a, _b;
        return ((_b = (_a = this.appRoles) === null || _a === void 0 ? void 0 : _a[app]) === null || _b === void 0 ? void 0 : _b.includes(role)) || false;
    }
    hasModelRole(model, role) {
        var _a, _b;
        return ((_b = (_a = this.appRoles) === null || _a === void 0 ? void 0 : _a['Lucy']) === null || _b === void 0 ? void 0 : _b.includes(`model.${model}.${role}`)) || false;
    }
    clearFrontendCache() {
        // Single unified cache — clears both backends (memory + localStorage lucy_cache_*).
        this.cacheManager.clearAllCache();
        this.pendingRequests.clear(); // in-flight request dedupe map
    }
}
export function createUXPContext(partialContext) {
    return new DefaultDashboardContext(partialContext);
}
export const UXPContext = React.createContext(createUXPContext(null));
